import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import { DATE_FORMAT, SnackbarApiErrorMessage } from '@ui-framework/index';

function invoiceNumber(original) {
  return original?.invoiceNumber || '';
}

function invoiceDate(original) {
  return original?.invoiceDate
    ? dayjs(original.invoiceDate).format(DATE_FORMAT.MEDIUM_DATE)
    : '';
}

function billingCycleStartDate(original) {
  return original?.billingCycleStartDate
    ? dayjs(original.billingCycleStartDate).format(DATE_FORMAT.MEDIUM_DATE)
    : '';
}

function billingCycleEndDate(original) {
  return original?.billingCycleEndDate
    ? dayjs(original.billingCycleEndDate).format(DATE_FORMAT.MEDIUM_DATE)
    : '';
}

function invoiceSummary(original) {
  const summary = original?.invoiceSummary ?? {};
  const entries = Object.entries(summary);

  if (!entries.length) {
    return '';
  }

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      {entries.map(([key, count]) => (
        <Typography
          key={key}
          component="span"
          variant="body2"
          sx={{ display: 'block' }}>
          {`${key
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/^./, character => character.toUpperCase())}: ${count}`}
        </Typography>
      ))}
    </Stack>
  );
}

function InvoiceDownloadButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/invoices/${row?.id}/download`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handleDownload = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'FAILED_TO_DOWNLOAD_INVOICE_FILE',
                  defaultMessage: 'Failed to download invoice file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'DOWNLOAD_INVOICE_FILE',
        defaultMessage: 'Download invoice file',
      })}>
      <IconButton size="small" onClick={handleDownload} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FileDownload />
        )}
      </IconButton>
    </Tooltip>
  );
}

function download(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <InvoiceDownloadButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

function InvoicePreviewButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/invoices/${row?.id}/preview`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handlePreview = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'FAILED_TO_GENERATE_PREVIEW_OF_INVOICE_FILE',
                  defaultMessage: 'Failed to generate preview of invoice file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'OPEN_PREVIEW_IN_A_NEW_TAB',
        defaultMessage: 'Open preview in a new tab',
      })}>
      <IconButton size="small" onClick={handlePreview} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FilePreview />
        )}
      </IconButton>
    </Tooltip>
  );
}

function preview(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <InvoicePreviewButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

export {
  invoiceNumber,
  invoiceDate,
  billingCycleStartDate,
  billingCycleEndDate,
  invoiceSummary,
  download,
  preview,
};
